import type { Candidate, PageBackgroundHit } from './templatePipeline';
import type { Prediction } from './templateClassify';
import type { LayoutTemplateAlbumCategory } from './api';

// Re-export so consumers (uploadFlow, SlotReviewPane) don't have to import
// from templatePipeline.ts just to get the type.
export type { PageBackgroundHit } from './templatePipeline';

const DB_NAME = 'storiz';
const STORE = 'templateUploads';
const KEY = 'current';
const TTL_MS = 60 * 60 * 1000; // 1 hour — ignore stale state from forgotten tabs

/**
 * One template waiting in the review-pane queue. The batch-upload flow produces
 * an array of these — SlotReviewPane consumes them one at a time, shifting the
 * queue forward on Save or Cancel until it's empty.
 */
export interface QueuedTemplate {
  svgString: string;
  fileName: string;
  candidates: Candidate[];
  predictions: Prediction[];
  /** Page-background `<rect>` / `<path>` hits found at detection time.
   *  Stripped from the overlay SVG at save time so the cream page-fill
   *  doesn't hide customer photos. */
  pageBackgrounds: PageBackgroundHit[];
  /** First non-null fill color from `pageBackgrounds` — persisted as the
   *  template row's `background_color` so the customer page background
   *  matches the template's native page color. null when no page bg was
   *  detected (caller defaults to '#FFFFFF'). */
  pageBackgroundColor: string | null;
  /** Optional id when editing an existing template (Replace SVG flow). */
  editingTemplateId?: string;
  /** Pre-fill values surfaced from TemplateEditDialog so the review pane shows
   *  the template's current metadata rather than a fresh filename-derived seed. */
  initialMetadata?: {
    name?: string;
    slug?: string;
    category?: LayoutTemplateAlbumCategory;
    isVisible?: boolean;
    isAiEligible?: boolean;
  };
}

export interface PendingTemplateUpload {
  /** Active template is `queue[0]`. Always >= 1 entry when a record exists. */
  queue: QueuedTemplate[];
  /** Wall-clock ms of when this payload was stored. Used for TTL. */
  savedAt: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
  });
}

export async function putPendingUpload(
  data: Omit<PendingTemplateUpload, 'savedAt'>,
): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put({ ...data, savedAt: Date.now() }, KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('idb put failed'));
      tx.onabort = () => reject(tx.error ?? new Error('idb put aborted'));
    });
  } finally {
    db.close();
  }
}

function isQueuedTemplate(x: unknown): x is QueuedTemplate {
  if (!x || typeof x !== 'object') return false;
  const q = x as Partial<QueuedTemplate>;
  return (
    typeof q.svgString === 'string' &&
    typeof q.fileName === 'string' &&
    Array.isArray(q.candidates) &&
    Array.isArray(q.predictions)
  );
}

export async function getPendingUpload(): Promise<PendingTemplateUpload | null> {
  const db = await openDb();
  try {
    const value = await new Promise<unknown>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(KEY);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('idb get failed'));
    });
    if (!value || typeof value !== 'object') return null;
    const v = value as Partial<PendingTemplateUpload>;
    if (typeof v.savedAt !== 'number' || Date.now() - v.savedAt > TTL_MS) {
      // Stale — best-effort wipe and pretend it isn't there
      try { await clearPendingUploadInternal(db); } catch { /* ignore */ }
      return null;
    }
    // Minimal shape validation so a deploy that changes the shape can't crash readers
    if (!Array.isArray(v.queue) || v.queue.length === 0 || !v.queue.every(isQueuedTemplate)) {
      return null;
    }
    return v as PendingTemplateUpload;
  } finally {
    db.close();
  }
}

/**
 * Pop the head of the queue and persist the rest. Returns the updated payload
 * (with `queue[0]` being the new active template), or `null` if the queue is
 * now empty — in which case the IDB record is deleted.
 *
 * Used by SlotReviewPane after Save (current template persisted, advance) or
 * Cancel (current template skipped, advance).
 */
export async function shiftPendingUpload(): Promise<PendingTemplateUpload | null> {
  const current = await getPendingUpload();
  if (!current) return null;
  const remaining = current.queue.slice(1);
  if (remaining.length === 0) {
    await clearPendingUpload();
    return null;
  }
  await putPendingUpload({ queue: remaining });
  return await getPendingUpload();
}

async function clearPendingUploadInternal(db: IDBDatabase): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('idb delete failed'));
  });
}

export async function clearPendingUpload(): Promise<void> {
  const db = await openDb();
  try {
    await clearPendingUploadInternal(db);
  } finally {
    db.close();
  }
}
